import classNames from 'classnames'
import { AnimatePresence, motion } from 'motion/react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Link, useNavigate } from '@lib/react-router-dom'

import { useProfileRoutesConditions } from '@pages/Profile/ProfileRoutes'
import { useTraderRoomRoutesConditions } from '@pages/Traders-Room/TradersRoomRoutes'

import { useMenu } from '@context/MenuContext'
import { useArabicSessionLanguage } from '@context/SessionSettingsContext'

import { CloseIcon } from '@icons/CloseIcon'
import { HamburgerIcon } from '@icons/HamburgerIcon'
import { TMEntityLogoFactory } from '@icons/TickmillLogoIcons'

import { ProductSwitch } from '@ui/ProductSwitch/ProductSwitch'
import { ToggleOption, ToggleSwitch } from '@ui/ToggleSwitch/ToggleSwitch'

import { Button, ButtonAppearance } from '../../button/Button'
import { Guard } from '../../guard/Guard'
import IconButton from '../../iconButton/IconButton'
import { ActionsMenu, Menu } from '../Menu'
import { useActionSections } from '../menuSections/useActionSections'
import { ActionHubButton } from './ActionHubButton/ActionHubButton'

import styles from './MobileNav.module.scss'

enum MenuItems {
  MENU = 'menu',
  ACTION_HUBS = 'actionHubs',
}

const container = {
  hidden: { insetInlineStart: '100%' },
  visible: { insetInlineStart: '0%' },
  exit: { insetInlineStart: '100%' },
}
const buttons = { hidden: { y: '100%' }, visible: { y: '0%' }, exit: { y: '100%' } }
const panels = {
  menu: { hidden: { x: '-100%' }, visible: { x: '0%' }, exit: { x: '-100%' } },
  action: { hidden: { x: '100%' }, visible: { x: '0%' }, exit: { x: '100%' } },
}

interface Props {
  isAuthed?: boolean
}

export const MobileNav: React.FC<Props> = (props) => {
  const { isAuthed } = props

  const redirectionLink = isAuthed ? '/' : ''

  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMenuOpen, toggleMenu, isScreenMenuOpen, closeMenu, setScreenMenuOpen } = useMenu()

  const { actionSections } = useActionSections()

  const isArabic = useArabicSessionLanguage()

  const [activeItem, setActiveItem] = useState<MenuItems>(MenuItems.MENU)

  const handleCloseScreen = () => {
    setScreenMenuOpen(false)
  }

  useEffect(() => {
    if (isMenuOpen) {
      setActiveItem(MenuItems.MENU)
    }
  }, [isMenuOpen])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleCloseScreen()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleCloseScreen])

  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()
  const { isFirstTimeDepositVisible } = useProfileRoutesConditions()

  const showDepositButton = isTradersRoomVisible || isFirstTimeDepositVisible

  const getToggleSwitchTabs = (): ToggleOption[] => {
    const tabs: ToggleOption[] = [
      {
        id: MenuItems.MENU,
        name: t('Menu'),
        onClick: () => setActiveItem(MenuItems.MENU),
      },
    ]

    if (actionSections.length) {
      tabs.push({
        id: MenuItems.ACTION_HUBS,
        name: t('Action Hub'),
        onClick: () => setActiveItem(MenuItems.ACTION_HUBS),
      })
    }

    return tabs
  }

  return (
    <div className={styles.navigationWrapper}>
      <nav
        className={classNames(styles.nav, { [styles.navRtl]: isArabic })}
        role='navigation'
        aria-label='main navigation'
      >
        <div className={styles.container}>
          <Link to={redirectionLink} onClick={handleCloseScreen}>
            <TMEntityLogoFactory />
          </Link>
          <div className={styles.navbarItemWrapper}>
            <IconButton className={styles.burgerButton} onClick={toggleMenu}>
              {isMenuOpen ? (
                <CloseIcon color='textWhite' size={16} />
              ) : (
                <HamburgerIcon color='textWhite' size={24} />
              )}
            </IconButton>
          </div>
        </div>
        <AnimatePresence mode='wait'>
          {isMenuOpen && (
            <motion.div
              className={styles.menu}
              variants={container}
              initial='hidden'
              animate='visible'
              exit='exit'
              transition={{ type: 'tween', duration: 0.3 }}
            >
              <div className={styles.menuContainer}>
                <Guard condition={isAuthed}>
                  <div className={styles.tabButtons}>
                    <ToggleSwitch tabs={getToggleSwitchTabs()} initialTab={activeItem} />
                  </div>
                </Guard>

                <AnimatePresence mode='popLayout'>
                  {activeItem === MenuItems.MENU && (
                    <motion.div
                      key='menu-panel'
                      variants={panels.menu}
                      initial='hidden'
                      animate='visible'
                      exit='exit'
                      transition={{ type: 'tween', duration: 0.3 }}
                      className={styles.contentWrapper}
                    >
                      <Menu />
                    </motion.div>
                  )}
                  {activeItem === MenuItems.ACTION_HUBS && (
                    <motion.div
                      key='action-panel'
                      variants={panels.action}
                      initial='hidden'
                      animate='visible'
                      exit='exit'
                      transition={{ type: 'tween', duration: 0.3 }}
                      className={styles.contentWrapper}
                    >
                      <ActionsMenu />
                      <div className={styles.productSwitchWrapper}>
                        <ProductSwitch />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <Guard condition={isAuthed && showDepositButton}>
          <AnimatePresence>
            {isMenuOpen && !isScreenMenuOpen && activeItem === MenuItems.MENU && (
              <motion.div
                key='menu-button'
                variants={buttons}
                initial='hidden'
                animate='visible'
                exit='exit'
                aria-label={isMenuOpen.toString()}
                transition={{ type: 'tween', duration: 0.3 }}
                className={styles.buttonContainer}
              >
                <Button
                  appearance={ButtonAppearance.Primary}
                  size='L'
                  fullWidth
                  onClick={() => {
                    navigate('/dashboard/traders-room/wallets/deposit')
                    closeMenu()
                  }}
                >
                  {t('Add Deposit')}
                </Button>
              </motion.div>
            )}
            {isMenuOpen && !isScreenMenuOpen && activeItem === MenuItems.ACTION_HUBS && (
              <motion.div
                key='action-button'
                variants={buttons}
                initial='hidden'
                animate='visible'
                exit='exit'
                transition={{ type: 'tween', duration: 0.3 }}
                className={styles.buttonContainer}
              >
                <ActionHubButton />
              </motion.div>
            )}
          </AnimatePresence>
        </Guard>
      </nav>
    </div>
  )
}
